import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { RolesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as RoleActions from "../../../actions/role";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./RolesPage.scss";

interface RolesPageProps {
    roles: Dtos.Role[];
    formFilter: IFormFilter;
    loadingRoles: boolean;
    loadRolesSuccess: boolean;
    loadRolesFailure: boolean;
    loadRolesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    modules: Dtos.ModulePermission[];
    pageSize: number;
    clearRoles: RoleActions.IRoleClearActionCreator;
    loadRoles: RoleActions.IRoleLoadActionCreator;
    filter: RoleActions.IRoleFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const RolesPage: FC<RolesPageProps> = ({
    roles,
    formFilter,
    loadingRoles,
    loadRolesSuccess,
    loadRolesFailure,
    loadRolesErrorCode,
    permissions,
    modules,
    pageSize,
    clearRoles,
    loadRoles,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showRoles = !loadingRoles && loadRolesSuccess;


    // Load roles
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(RolesBreadcrumbs());

            loadRoles();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        RolesBreadcrumbs,
        loadRoles,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadRolesFailure) {
            navigate(`/error/${loadRolesErrorCode}`);
        }
    }, [loadRolesFailure, loadRolesErrorCode, navigate]);

    // Cleanup effect to clear the roles on component unmount
    useEffect(() => {
        return () => {
            clearRoles();
        };
    }, [clearRoles]);

    const getPermissions = (props) => {
        return props.original.modules[props.column.id] || "";
    };

    const createTable = useMemo(() => {
        if (!roles || roles.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no roles to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = roles.length > pageSize;

        let columns: any[] = [];
        columns.push({
            id: "id",
            Header: " ",
            accessor: "id",
            maxWidth: 50,
            Cell: (_props) => (
                <FontAwesomeIcon
                    size="lg"
                    icon={FontAwesomeIcons.Light.LOOKUP}
                    fixedWidth
                />
            ),
        });
        columns.push({
            id: "value",
            Header: "Name",
            accessor: "value",
            minWidth: 220,
        });
        if (modules && modules.length > 0) {
            modules.forEach((module) => {
                columns.push({
                    id: module.name,
                    Header: module.name?.replace("TMD", ""),
                    accessor: module.name,
                    minWidth: 100,
                    Cell: (props) => (
                        <>
                            <span
                                data-for={`${props.original.id}-${props.column.id}MarkToolTip`}
                                data-tip={getPermissions(props).replaceAll(
                                    ",",
                                    ",<br />"
                                )}
                                className="truncate"
                            >
                                {getPermissions(props) == "" ? (
                                    ""
                                ) : (
                                    <FontAwesomeIcon
                                        icon={FontAwesomeIcons.Light.CHECK}
                                        fixedWidth
                                    />
                                )}
                            </span>
                            <ReactTooltip
                                id={`${props.original.id}-${props.column.id}MarkToolTip`}
                                multiline={true}
                                data-html={true}
                                insecure={true}
                            />
                        </>
                    ),
                });
            });
        }

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={roles}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? pageSize : roles.length}
                    pageSize={showPagination ? pageSize : roles.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no roles to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/general/roles/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={columns}
                />
            </div>
        );
    }, [roles, pageSize, modules, getPermissions, PAGE_SIZE_OPTIONS, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Roles</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.RolesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/general/roles/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showRoles ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showRoles ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, createTable]);

    return (
        <LookupsLayout
            loading={loadingRoles}
            permission={Dtos.Permission.RolesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<RolesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        roles: state.roles.formFilteredData!,
        formFilter: state.roles.formFilter,
        loadingRoles:
            state.roles.loadState &&
            state.roles.loadState.status == RequestState.Pending,
        loadRolesSuccess:
            state.roles.loadState &&
            state.roles.loadState.status == RequestState.Success,
        loadRolesFailure:
            state.roles.loadState &&
            state.roles.loadState.status == RequestState.Failure,
        loadRolesErrorCode: state.roles.loadState.errorCode,
        permissions: permissions,
        modules: state.roles.modules,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<RolesPageProps> => {
    return {
        loadRoles: bindActionCreators(RoleActions.LoadRole, dispatch),
        clearRoles: bindActionCreators(RoleActions.Clear, dispatch),
        filter: bindActionCreators(RoleActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RolesPage);
